import React from 'react';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { AlertTriangle, Flame, RotateCcw, Trash2 } from 'lucide-react';

/**
 * iter167 — Extracted from SubscribersList.jsx to keep that file under the
 * 700-line maintainability limit. These are the three destructive
 * confirmation dialogs shared by both single-row and bulk actions.
 */

export function SoftDeleteDialog({ user, loading, onCancel, onConfirm }) {
  return (
    <Dialog open={!!user} onOpenChange={onCancel}>
      <DialogContent data-testid="delete-subscriber-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-amber-400">
            <AlertTriangle className="h-5 w-5" />
            Delete Subscriber
          </DialogTitle>
          <DialogDescription>
            Move <strong>{user?.full_name || user?.email}</strong> to the Deleted view?
            Their account will be hidden from the main list but all their data is
            preserved. You can restore them any time from the <em>Deleted</em> filter.
            <br /><br />
            To permanently wipe their data, use <strong>Purge permanently</strong> from
            the Deleted view.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="outline" onClick={onCancel} disabled={loading} data-testid="cancel-delete">
            Cancel
          </Button>
          <Button variant="destructive" onClick={onConfirm} disabled={loading} data-testid="confirm-delete">
            {loading ? 'Deleting...' : 'Move to Deleted'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function PurgePermanentlyDialog({ user, loading, onCancel, onConfirm }) {
  return (
    <Dialog open={!!user} onOpenChange={onCancel}>
      <DialogContent data-testid="purge-subscriber-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-rose-400">
            <Flame className="h-5 w-5" />
            Permanently Purge Subscriber
          </DialogTitle>
          <DialogDescription>
            This will PERMANENTLY delete <strong>{user?.full_name || user?.email}</strong> and
            all of their data:
            <ul className="list-disc list-inside mt-2 space-y-1">
              <li>All data sources &amp; signals</li>
              <li>All content, folders, lists, filters</li>
              <li>All share links &amp; project milestones</li>
            </ul>
            <strong className="text-rose-400 block mt-2">This action cannot be undone.</strong>
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="outline" onClick={onCancel} disabled={loading} data-testid="cancel-purge">
            Cancel
          </Button>
          <Button
            variant="destructive"
            onClick={onConfirm}
            disabled={loading}
            className="bg-rose-600 hover:bg-rose-700"
            data-testid="confirm-purge"
          >
            {loading ? 'Purging...' : 'Purge permanently'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function BulkActionDialog({ action, count, loading, onCancel, onConfirm }) {
  return (
    <Dialog open={!!action} onOpenChange={onCancel}>
      <DialogContent data-testid="bulk-confirm-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {action === 'purge' ? (
              <><Flame className="h-5 w-5 text-rose-400" /> Confirm bulk purge</>
            ) : action === 'restore' ? (
              <><RotateCcw className="h-5 w-5 text-emerald-400" /> Confirm bulk restore</>
            ) : (
              <><Trash2 className="h-5 w-5 text-amber-400" /> Confirm bulk soft-delete</>
            )}
          </DialogTitle>
          <DialogDescription>
            {action === 'purge' ? (
              <>
                You are about to <strong className="text-rose-400">PERMANENTLY purge {count} subscriber(s)</strong> and all their data.
                This action cannot be undone.
              </>
            ) : action === 'restore' ? (
              <>Restore {count} soft-deleted subscriber(s) back to the main list?</>
            ) : (
              <>Move {count} subscriber(s) to the Deleted view? You can restore them later.</>
            )}
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="outline" onClick={onCancel} disabled={loading} data-testid="cancel-bulk">
            Cancel
          </Button>
          <Button
            variant="destructive"
            onClick={onConfirm}
            disabled={loading}
            className={action === 'purge' ? 'bg-rose-600 hover:bg-rose-700' : ''}
            data-testid="confirm-bulk"
          >
            {loading ? 'Processing...' : `Confirm ${action}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
